import React, { useState, useEffect, useCallback } from 'react';
import { usePriestAuth } from '../../context/PriestAuthContext';

const STATUS_TABS = [
  { id: '',          label: 'All' },
  { id: 'pending',   label: 'Upcoming' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
];

const EMPTY = {
  poojaType: '', yajamaniName: '', phone: '', location: '',
  eventDate: '', eventTime: '', reminderDate: '', reminderTime: '',
  notes: '', fee: '', status: 'pending',
};

export default function BookingsManager({ onChange }) {
  const { priestFetch } = usePriestAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search)       params.set('q', search);
      const data = await priestFetch(`/api/priest/bookings?${params.toString()}`);
      setBookings(data.bookings || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [priestFetch, statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); setErr(''); };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({
      poojaType: b.poojaType || '', yajamaniName: b.yajamaniName || '',
      phone: b.phone || '', location: b.location || '',
      eventDate: b.eventDate || '', eventTime: (b.eventTime || '').slice(0,5),
      reminderDate: b.reminderDate || '', reminderTime: (b.reminderTime || '').slice(0,5),
      notes: b.notes || '', fee: b.fee || '', status: b.status || 'pending',
    });
    setShowForm(true); setErr('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    try {
      const url = editing ? `/api/priest/bookings/${editing}` : '/api/priest/bookings';
      await priestFetch(url, {
        method: editing ? 'PUT' : 'POST',
        body: JSON.stringify(form),
      });
      setShowForm(false);
      await load();
      onChange?.();
    } catch (e) { setErr(e.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this booking?')) return;
    try {
      await priestFetch(`/api/priest/bookings/${id}`, { method: 'DELETE' });
      await load();
      onChange?.();
    } catch (e) { alert(e.message); }
  };

  const setStatus = async (id, status) => {
    try {
      await priestFetch(`/api/priest/bookings/${id}`, {
        method: 'PUT', body: JSON.stringify({ status }),
      });
      await load();
      onChange?.();
    } catch (e) { alert(e.message); }
  };

  return (
    <div className="bm-wrapper">
      <div className="bm-toolbar">
        <div className="bm-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.id || 'all'}
              className={`bm-tab ${statusFilter === t.id ? 'active' : ''}`}
              onClick={() => setStatusFilter(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="bm-toolbar-right">
          <input className="bm-search" placeholder="🔍 Search client, pooja..."
            value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-primary" onClick={openNew}>+ New Booking</button>
        </div>
      </div>

      {showForm && (
        <div className="bm-form-card">
          <h3>{editing ? '✏️ Edit Booking' : '➕ New Booking'}</h3>
          {err && <div className="auth-error">⚠️ {err}</div>}
          <form onSubmit={onSubmit}>
            <div className="form-row-2">
              <Field label="Pooja Type *" name="poojaType" value={form.poojaType}
                onChange={v => setForm(p => ({ ...p, poojaType: v }))}
                placeholder="e.g. Satyanarayana Vratam" />
              <Field label="Yajamani (Client) *" name="yajamaniName" value={form.yajamaniName}
                onChange={v => setForm(p => ({ ...p, yajamaniName: v }))} />
            </div>
            <div className="form-row-2">
              <Field label="Phone" name="phone" value={form.phone}
                onChange={v => setForm(p => ({ ...p, phone: v }))} />
              <Field label="Location" name="location" value={form.location}
                onChange={v => setForm(p => ({ ...p, location: v }))} />
            </div>
            <div className="form-row-2">
              <Field label="Event Date *" type="date" name="eventDate" value={form.eventDate}
                onChange={v => setForm(p => ({ ...p, eventDate: v }))} />
              <Field label="Event Time" type="time" name="eventTime" value={form.eventTime}
                onChange={v => setForm(p => ({ ...p, eventTime: v }))} />
            </div>
            <div className="form-row-2">
              <Field label="Reminder Date" type="date" name="reminderDate" value={form.reminderDate}
                onChange={v => setForm(p => ({ ...p, reminderDate: v }))} />
              <Field label="Reminder Time" type="time" name="reminderTime" value={form.reminderTime}
                onChange={v => setForm(p => ({ ...p, reminderTime: v }))} />
            </div>
            <div className="form-row-2">
              <Field label="Fee (₹)" type="number" name="fee" value={form.fee}
                onChange={v => setForm(p => ({ ...p, fee: v }))} />
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea rows="3" value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
            <div className="bm-form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">
                {editing ? 'Update' : 'Create'} Booking
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="bm-empty">Loading...</div>
      ) : bookings.length === 0 ? (
        <div className="bm-empty">
          <div style={{ fontSize: '3rem' }}>📭</div>
          <h3>No bookings yet</h3>
          <p>Create your first booking to get started.</p>
        </div>
      ) : (
        <div className="bm-list">
          {bookings.map(b => (
            <div key={b.id} className="bm-card">
              <div className="bm-card-head">
                <div>
                  <h4>{b.poojaType}</h4>
                  <div className="bm-card-sub">
                    👤 {b.yajamaniName}
                    {b.phone && <> • 📞 {b.phone}</>}
                  </div>
                </div>
                <span className={`pd-status pd-status-${b.status}`}>{b.status}</span>
              </div>
              <div className="bm-card-meta">
                <span>📅 {b.eventDate}{b.eventTime ? ` ${b.eventTime.slice(0,5)}` : ''}</span>
                {b.location && <span>📍 {b.location}</span>}
                {Number(b.fee) > 0 && <span>💰 ₹{b.fee}</span>}
              </div>
              {b.notes && <div className="bm-card-notes">📝 {b.notes}</div>}
              <div className="bm-card-actions">
                {b.phone && (
                  <a className="bm-btn bm-btn-call" href={`tel:${b.phone}`}>📞 Call</a>
                )}
                {b.phone && (
                  <a className="bm-btn bm-btn-msg" href={`sms:${b.phone}`}>💬 SMS</a>
                )}
                {b.status !== 'completed' && (
                  <button className="bm-btn bm-btn-done"
                    onClick={() => setStatus(b.id, 'completed')}>✓ Mark Done</button>
                )}
                <button className="bm-btn bm-btn-edit" onClick={() => openEdit(b)}>✏️ Edit</button>
                <button className="bm-btn bm-btn-del" onClick={() => remove(b.id)}>🗑️ Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, type = 'text', name, value, onChange, placeholder }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      <input type={type} name={name} value={value}
        placeholder={placeholder}
        onChange={e => onChange(e.target.value)} />
    </div>
  );
}
